import {
  createDirectus,
  readAssetRaw,
  readField,
  readFiles,
  readFolder,
  readFolders,
  readItems,
  rest,
  RestCommand,
  withToken,
} from "@directus/sdk";
import {Feature} from "../../interfaces/directus/Feature";
import {FileSearch} from "../../interfaces/documentation/FileSearch";
import {Folder} from "../../interfaces/documentation/Folder";
import {Release, StatusEnum} from "../../interfaces/directus/Release";
import {REACT_APP_DIRECTUS_URL} from "../../config";
import {Field} from "../../interfaces/documentation/Field";
import {getUser} from "../KeycloakService";
import {CurrentAuthorization} from "../../interfaces/CurrentAuthorization";

interface Schema {
  features: Feature[]; // regular collections are array types
  partners_releases: Release[];
}

const client = createDirectus<Schema>(REACT_APP_DIRECTUS_URL).with(rest());
const apiDirectus = {
  DOCUMENTATION_FOLDER_ID: "5b2ccd67-6287-4c2d-acba-e02b5729014f",

  request: function <Type>(command: RestCommand<unknown, Schema>): Promise<Type> {
    const user = getUser();
    return client.request<Type>(withToken<Schema, Type>(user?.access_token ?? "", command));
  },

  getFeatures: async function (): Promise<Array<Release>> {
    return await this.request(
      readItems("partners_releases", {
        filter: {
          status: {_eq: StatusEnum.PUBLISHED},
          // Les nouveautés de plus d'un an ne sont plus affichées
          published: {_gte: "$NOW(-1 year)"},
        } as any,
        fields: ["id", "published", {features: ["id", "title", "description", "url"]}],
        sort: ["-published"],
      }),
    );
  },

  getFiles: async function (
    folders: Array<Folder>,
    searchValue: string | null,
    documentTypes: string[] | null,
    models: string[] | null,
    languages: string[] | null,
    lastVersion: boolean | null,
    lifeCycles: string[] | null,
    authorization: CurrentAuthorization | null,
  ): Promise<Array<FileSearch>> {
    const options: any = {
      filter: {
        type: {
          _in: [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/x-zip-compressed",
            "text/csv",
            "image/jpeg",
            "application/vnd.oasis.opendocument.presentation",
            "application/vnd.oasis.opendocument.spreadsheet",
            "application/vnd.oasis.opendocument.text",
            "application/vnd.ms-powerpoint",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            "application/x-rar-compressed",
          ],
        },
        _or: [],
        _and: [],
      },
      sort: ["-Publication", "-id"],
      limit: -1,
    };
    const permissions = authorization?.documentationPermissions;

    if (searchValue) {
      options.filter._or.push(
        {
          title: {
            _icontains: searchValue,
          },
        },
        {
          description: {
            _icontains: searchValue,
          },
        },
      );
    }

    if (languages) {
      options.filter._and.push({
        _or: languages.map((language) => ({
          Langage: {
            _eq: language,
          },
        })),
      });
    }

    if (documentTypes) {
      options.filter._and.push({
        Type_de_document: {
          _in: documentTypes,
        },
      });
    }

    if (models) {
      options.filter._and.push({
        _or: models.map((model) => ({
          Modele: {
            _contains: model,
          },
        })),
      });
    }

    if (lastVersion) {
      options.filter._and.push({
        _or: [
          {
            document_a_jour: {
              _eq: lastVersion,
            },
          },
          {
            document_a_jour: {
              _null: true,
            },
          },
        ],
      });
    }

    if (lifeCycles) {
      options.filter._and.push({
        _or: lifeCycles.map((lifeCycle) => ({
          Cycle_de_vie: {
            _contains: lifeCycle,
          },
        })),
      });
    }

    if (permissions) {
      options.filter._and.push({
        Type_de_document: {
          _in: permissions,
        },
      });
    }

    const allFiles: FileSearch[] = await this.request(readFiles(options));
    const filteredFiles = allFiles.filter((file) => {
      return folders.some((f) => f.id === file.folder);
    });
    return filteredFiles;
  },

  getFilesFromFolder: async function (folderId: string): Promise<Array<FileSearch>> {
    return await this.request(
      readFiles({
        filter: {
          folder: {
            _eq: folderId,
          },
        },
      }),
    );
  },

  // Uint8Array<ArrayBuffer> et non le Uint8Array<ArrayBufferLike> par défaut : un flux réseau n'est
  // jamais adossé à un SharedArrayBuffer, et BlobPart n'accepte que la première forme.
  getImage: async function (fileId: string): Promise<ReadableStream<Uint8Array<ArrayBuffer>>> {
    return await this.request(
      readAssetRaw(fileId, {
        width: 90,
        height: 90,
        quality: 100,
      }),
    );
  },

  getFolder: async function (id: string): Promise<Folder> {
    return await this.request(
      readFolder(id, {
        fields: ["*"],
      }),
    );
  },

  getAllFolders: async function (): Promise<Folder[]> {
    return await this.request(
      readFolders({
        fields: ["*"],
      }),
    );
  },

  getFolderDescendants: async function (parentFolderId: string): Promise<Folder[]> {
    const response = await this.request(
      readFolders({
        fields: ["id", "parent"],
        limit: -1,
      }),
    );
    const allFolders: Folder[] = (response as any)?.data ?? response ?? [];

    const foldersByParent = new Map<string, Folder[]>();
    for (const folder of allFolders) {
      const parent = folder.parent;
      if (parent === null) {
        continue;
      }

      if (!foldersByParent.has(parent)) {
        foldersByParent.set(parent, []);
      }
      foldersByParent.get(parent)!.push(folder);
    }

    const descendants: Folder[] = [];
    const stack = [parentFolderId];
    while (stack.length > 0) {
      const currentId = stack.pop()!;
      const children = foldersByParent.get(currentId) ?? [];
      for (const child of children) {
        descendants.push(child);
        stack.push(child.id);
      }
    }

    return descendants;
  },

  getCustomField: async function (fieldName: string): Promise<Field> {
    return await this.request(readField("directus_files", fieldName));
  },

  getMandatEnedis: async function (mandatType: number): Promise<FileSearch[]> {
    return await this.request(
      readFiles({
        filter: {
          _and: [{Mandat_Enedis: {_eq: mandatType}}, {document_a_jour: {_eq: true}}],
        } as any,
        fields: ["*"],
      }),
    );
  },
};

export default apiDirectus;
